import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { applicationReadiness } from '../../domain/kyc/readiness';
import { wholesaleModeFor } from '../../domain/kyc/wholesale';
import { useRegistry } from '../../store/RegistryStore';
import { suggestRisk } from '../../domain/kyc/riskRating';
import { applicationPayment, closeExclusion } from '../../domain/raise/raiseCalc';
import { DEMO_TODAY, money } from '../../lib/format';
import { sumBy, useTenantScope } from '../../store/selectors';
import { SectionCard } from '../../ui/primitives';

export default function AttentionPanel() {
  const scope = useTenantScope();
  const { state } = useRegistry();
  const navigate = useNavigate();

  const items = useMemo(() => {
    const holderIdsWithUnits = new Set(scope.holdings.map((holding) => holding.holderId));
    const active = scope.holders.filter((holder) => holderIdsWithUnits.has(holder.id));
    const noTfn = active.filter(
      (holder) => holder.tfnStatus === 'NOT_QUOTED' && holder.residency === 'AU',
    );
    const withheld = sumBy(
      scope.ledger.filter((entry) => entry.type === 'WITHHOLDING'),
      (entry) => entry.amount,
    );
    const kycOverdue = active.filter((holder) => holder.kycStatus === 'EXPIRED');
    const unverifiedBank = active.filter((holder) => !holder.bank.verified);
    const overdueCalls = scope.calls
      .filter((call) => call.status === 'ISSUED' && call.dueDate < DEMO_TODAY)
      .flatMap((call) => call.lines.filter((line) => line.status !== 'PAID'));
    const foreign = active.filter((holder) => holder.residency !== 'AU');
    const draftDistributions = scope.distributions.filter((item) => item.status === 'DRAFT');
    const closeReady = scope.offers
      .filter((offer) => offer.status === 'PUBLISHED' && offer.issueAtClose)
      .map((offer) => {
        const unitClass = scope.classById.get(offer.classId);
        const ready = scope.applications.filter((application) => {
          if (application.classId !== offer.classId || application.status === 'ISSUED')
            return false;
          const holder = scope.holderByEntityId.get(application.investmentEntityId);
          return !closeExclusion(
            application,
            applicationPayment(application, unitClass),
            applicationReadiness(
              application,
              holder,
              scope.kycCases,
              DEMO_TODAY,
              undefined,
              wholesaleModeFor(state, offer.fundId),
            ),
          );
        });
        return { offer, ready, total: sumBy(ready, (application) => application.amount) };
      })
      .filter(
        (item) =>
          item.ready.length > 0 &&
          !scope.issueRuns.some(
            (run) => run.offerId === item.offer.id && run.status !== 'REJECTED',
          ),
      );
    const valueByHolder = new Map<string, number>();
    for (const holding of scope.holdings)
      valueByHolder.set(
        holding.holderId,
        (valueByHolder.get(holding.holderId) ?? 0) + holding.value,
      );
    const riskChanges = active.filter(
      (holder) =>
        suggestRisk(holder, scope.kycCases, valueByHolder.get(holder.id) ?? 0).rating !==
        holder.riskRating,
    );
    const quarterSent = scope.reportRuns.some(
      (run) => run.quarterEnd === '2026-09-30' && run.status !== 'REJECTED',
    );
    return [
      ...closeReady.map(({ offer, ready, total }) => ({
        label: `${scope.fundById.get(offer.fundId)?.code}: ${ready.length} applicants paid and ready to issue`,
        detail: `${money(total)} ready for financial close`,
        to: `/manager/offers/${offer.id}/close`,
        show: true,
      })),
      {
        label: 'September quarter report not yet sent',
        detail: 'One run sends the report and every investor’s statement',
        to: '/manager/communications',
        show: !quarterSent && scope.tenant.id === 't-fides',
      },
      {
        label: `${riskChanges.length} risk ${riskChanges.length === 1 ? 'rating' : 'ratings'} the register suggests changing`,
        detail: 'Review and confirm, or record why not',
        to:
          riskChanges.length === 1
            ? `/manager/holders/sample/${riskChanges[0].id}`
            : '/manager/holders/sample',
        show: riskChanges.length > 0,
      },
      {
        label: `${noTfn.length} holders have not quoted a TFN or ABN`,
        detail: `${money(withheld)} withheld this financial year at 47%`,
        to: '/manager/holders/sample?filter=tfn',
        show: noTfn.length > 0,
      },
      {
        label: `${kycOverdue.length} periodic KYC refreshes overdue`,
        detail: 'Investors have a task in the app to re-verify',
        to: '/manager/kyc',
        show: kycOverdue.length > 0,
      },
      {
        label: `${unverifiedBank.length} bank accounts not verified`,
        detail: 'Payments to these holders are held until verified',
        to: '/manager/holders/sample?filter=bank',
        show: unverifiedBank.length > 0,
      },
      {
        label: `${overdueCalls.length} capital call payments overdue`,
        detail: 'Default interest applies under the trust deed',
        to: '/manager/calls',
        show: overdueCalls.length > 0,
      },
      {
        label: `${draftDistributions.length} distribution drafts not yet submitted`,
        detail: 'Calculated but not sent for approval',
        to: '/manager/distributions',
        show: draftDistributions.length > 0,
      },
      {
        label: `${foreign.length} non-resident holders`,
        detail: 'Non-resident withholding and CRS/FATCA reporting apply',
        to: '/manager/holders/sample?filter=foreign',
        show: foreign.length > 0,
      },
    ].filter((item) => item.show);
  }, [scope, state]);

  return (
    <SectionCard
      title="Needs attention"
      subtitle="Data quality and compliance exceptions"
      noPadding
    >
      <List dense disablePadding>
        {items.map((item) => (
          <ListItemButton
            key={item.label}
            onClick={() => navigate(item.to)}
            sx={{ px: 2.5, borderTop: '1px solid', borderColor: 'divider' }}
          >
            <ListItemIcon sx={{ minWidth: 34 }}>
              <ErrorOutlineIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              secondary={item.detail}
              primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
            />
            <ChevronRightIcon fontSize="small" color="action" />
          </ListItemButton>
        ))}
      </List>
    </SectionCard>
  );
}
